<template>
    <Sections>
        <SectionMembershipJumbo
            v-if="product"
            :product="product"
            :selectedPlan="selectedPlan"
            :selectPlan="selectPlan"
        >
            <template v-slot:checkout>
                <FormMembershipPlanChip
                    :selectedPlan="selectedPlan"
                    :clearPlan="clearPlan"
                ></FormMembershipPlanChip>
                <ContainerSubscriptionLogin v-if="!isLoggedIn"></ContainerSubscriptionLogin>
                <ContainerSubscriptionCreate
                    v-else
                    :idSubscriptionPlan="selectedPlan.id_subscription_plan"
                    :subscriptionPlanAmount="selectedPlan.subscription_plan_amount"
                    :freeTrial="freeTrialAvailable"
                    @scrollTop="scrollTop"
                ></ContainerSubscriptionCreate>
            </template>
        </SectionMembershipJumbo>
        <SectionImage
            v-if="product"
            color="bg-zinc-100"
            :image="$config.public.uploads + 'images-general/course-image-grid-3.png'"
            imageAlt="a selection of course images"
            imageAspectRatio="aspect-[16/9]"
            heading="Full Access!"
            headingComponent="h3"
            content="<p>Unlock every course and lesson - over 800 lessons only available to AAA Members.</p>"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :flip="true"
        ></SectionImage>
        <SectionImage
            v-if="product"
            :image="$config.public.uploads + 'images-lesson/lesson-1-thumb-3-1.jpg'"
            imageAlt="a selection of course images"
            imageAspectRatio="aspect-[16/9]"
            heading="New Beginner Course"
            headingComponent="h3"
            content="<p>My renowned beginner guitar course has been completely redesigned and re-filmed for 2025. The original course helped thousands pick up the guitar with confidence - now the new and improved version is available exclusively on Andy Guitar.</p>"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :flip="false"
        ></SectionImage>
        <SectionVideoWithText
            v-if="product"
            color="bg-zinc-100"
            :video="$config.public.uploads + 'videos-general/live-stream-light-600-trim.mp4'"
            heading="Live Streams"
            headingComponent="h3"
            content="<p>Join my weekly live streams and work directly with me on your playing, with feedback to move you forward and get inspired!</p>"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :flip="true"
        ></SectionVideoWithText>
        <SectionVideoWithText
            v-if="product"
            :video="$config.public.uploads + 'videos-general/interactive-tab-light-600-trim.mp4'"
            heading="Interactive Tab"
            headingComponent="h3"
            content="<p>Play along with Andy with hours of exclusive interactive tab. Slow down the songs, loop sections, view fretboard diagrams and more.</p>"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :flip="false"
        ></SectionVideoWithText>
        <SectionImage
            color="bg-zinc-100"
            heading="Discord Community"
            headingComponent="h3"
            content="<p>Membership give you access to the Andy Guitar Discord Community. Share your progress, get updates on what’s happening and connect with other members.</p>"
            :image="$config.public.uploads + 'images-general/guitar-breaks-may-2022-best-1.jpeg'"
            imageAlt="Andy Guitar members at an in person workshop"
            imageLoading="lazy"
            imageAspectRatio="1278/959"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :flip="true"
        ></SectionImage>
        <SectionCards
            v-if="product"
            heading="Why Andy Guitar?"
            headingComponent="h3"
            :buttonText="buttonText"
            :buttonColor="buttonColor"
            :buttonClick="scrollTop"
            :cards="[
                {
                    image: $config.public.uploads + 'images-general/yt-ag.jpg',
                    imageAlt: 'youtube logo',
                    imageLoading: 'lazy',
                    imageAspectRatio: '16/9',
                    heading: 'Trusted by 3 million people',
                    excerpt: 'Andy Guitar is one of the top names in music education, trusted by 3 million YouTube subscribers worldwide.',
                },
                {
                    image: $config.public.uploads + 'images-general/platform-screens.png',
                    imageAlt: 'Andy Guitar website displayed on different devices',
                    imageLoading: 'lazy',
                    imageAspectRatio: '16/9',
                    heading: 'World-class learning platform',
                    excerpt: 'The Andy Guitar website is among the most comprehensive and intuitive learning platforms in the world.'
                },
                {
                    image: $config.public.uploads + 'images-general/guest-teachers-guitar-lessons.jpg',
                    imageAlt: 'guest music teacher collage',
                    imageLoading: 'lazy',
                    imageAspectRatio: '16/9',
                    heading: 'Guest Teachers',
                    excerpt: 'Membership includes additional courses by guest teachers, who specialize in specific areas of guitar and more.',
                },
            ]"
        ></SectionCards>
        <SectionJumbo
            v-if="product"
            color="bg-zinc-900"
            padding="py-10 sm:py-12 lg:py-24"
            :image="$config.public.images + 'aaa-jumbo.jpg'"
            imageAlt="Andy Guitar camera roll "
            imagePosition="object-center"
            imageOpacity="opacity-10"
            :heading="product.subscription_product_name"
            headingComponent="h3"
            headingColor="text-white"
            content="<ArticleMembershipSellingPoints />"
            :buttonText="buttonText"
            :buttonClick="scrollTop"
            :textCentered="false"
            imageLoading="lazy"
            fetchPriority="low"
        ></SectionJumbo>
        <LoadingSection :loading="!product" :error="error"></LoadingSection>
    </Sections>
</template>

<script>
import { mapState, mapActions } from 'pinia';

export default {
    name: 'ContainerMembershipCheckout',
    computed: {
        ...mapState(useStoreAuth, {
            isLoggedIn: 'isLoggedIn',
        }),
        ...mapState(useStoreMembership, {
            product: 'product',
            selectedPlan: 'plan',
            error: 'error',
        }),
        freeTrialAvailable () {
            if (!this.product) return false;
            return (this.product.subscription_product_trial_interval && this.product.subscription_product_trial_length);
        },
        buttonText () {
            if (this.freeTrialAvailable) return 'Start Free Trial';
            if (this.selectedPlan) return 'Continue';
            return 'Choose Plan';
        },
        buttonColor () {
            return '';
        },
    },
    methods: {
        ...mapActions(useStoreMembership, {
            selectPlan: 'selectPlan',
            clearPlan: 'clearPlan',
        }),
        scrollTop () {
            if (import.meta.client) window.scrollTo({top: 0, behavior: 'smooth'});
        },
    },
    watch: {
        selectedPlan () {
            this.scrollTop();
        },
    },
};
</script>
